import { FC, useState } from "react";
import type { WorkOrderStatus } from "@myapp/contract";
import {
  getWorkOrderStatusLabel,
  useUpdateWorkOrderStatus,
} from "../../../entities/work-order";
import { getErrorMessage } from "../../../shared/helpers/getErrorMessage";
import { WorkOrderStatusMenu } from "./WorkOrderStatusMenu";
import { ConfirmStatusChangeDialog } from "./ConfirmStatusChangeDialog";

interface Props {
  workOrderId: string;
  status: WorkOrderStatus;
  compact?: boolean;
}

/**
 * Menu → confirm → save, for one Auftrag. The caller decides who gets to see it; this only does it.
 *
 * On success the mutation has already written the new status into the detail cache, so the title
 * changes by itself and this component only has to close the dialog and say so to a screen reader.
 */
export const WorkOrderStatusControl: FC<Props> = ({ workOrderId, status, compact }) => {
  const [pending, setPending] = useState<WorkOrderStatus | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const update = useUpdateWorkOrderStatus();

  const cancel = () => {
    if (update.isPending) return;
    setPending(null);
    update.reset();
  };

  const confirm = async () => {
    if (!pending || update.isPending) return;
    try {
      await update.mutateAsync({ workOrderId, dto: { status: pending } });
      setAnnouncement(`Status geändert auf ${getWorkOrderStatusLabel(pending)}`);
      setPending(null);
    } catch {
      // Stays open: the error is shown in the dialog, next to the button that retries.
    }
  };

  return (
    <>
      <WorkOrderStatusMenu
        status={status}
        compact={compact}
        onSelect={(next) => {
          update.reset();
          setPending(next);
        }}
      />
      <ConfirmStatusChangeDialog
        to={pending}
        from={status}
        isPending={update.isPending}
        error={update.isError ? getErrorMessage(update.error) : null}
        onConfirm={confirm}
        onCancel={cancel}
      />
      <span role="status" className="sr-only">
        {announcement}
      </span>
    </>
  );
};
